import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'info';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  dark?: boolean; // For use in modals with white backgrounds
}

const StyledButton = styled.button<ButtonProps & { dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid transparent;

  ${({ size = 'md' }) => {
    switch (size) {
      case 'sm':
        return css`
          padding: ${theme.spacing[2]} ${theme.spacing[3]};
          font-size: ${theme.typography.fontSize.sm};
          min-height: 32px;
        `;
      case 'lg':
        return css`
          padding: ${theme.spacing[4]} ${theme.spacing[6]};
          font-size: ${theme.typography.fontSize.lg};
          min-height: 48px;
        `;
      default:
        return css`
          padding: ${theme.spacing[3]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.base};
          min-height: 40px;
        `;
    }
  }}

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

  ${({ disabled, loading }) =>
    (disabled || loading) &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}

  ${({ variant = 'primary', dark = false }) => {
    switch (variant) {
      case 'secondary':
        return css`
          background-color: ${dark ? theme.colors.secondary[100] : theme.colors.secondary[600]};
          color: ${dark ? '#000000' : theme.colors.white};
          border: 1px solid ${dark ? theme.colors.dark.border.medium : theme.colors.secondary[600]};
          box-shadow: ${dark ? '0 1px 2px rgba(0, 0, 0, 0.1)' : '0 2px 4px rgba(108, 117, 125, 0.3)'};

          &:hover:not(:disabled) {
            background-color: ${dark ? theme.colors.secondary[200] : theme.colors.secondary[700]};
            border-color: ${dark ? theme.colors.secondary[300] : theme.colors.secondary[700]};
            box-shadow: ${dark ? '0 2px 4px rgba(0, 0, 0, 0.15)' : '0 4px 8px rgba(108, 117, 125, 0.4)'};
            transform: translateY(-1px);
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: ${dark ? theme.colors.primary[600] : theme.colors.white};
          border: 2px solid ${dark ? theme.colors.primary[500] : 'rgba(255, 255, 255, 0.3)'};
          ${!dark && 'backdrop-filter: blur(10px);'}

          &:hover:not(:disabled) {
            background-color: ${dark ? theme.colors.primary[50] : 'rgba(255, 255, 255, 0.1)'};
            border-color: ${dark ? theme.colors.primary[600] : 'rgba(255, 255, 255, 0.5)'};
            color: ${dark ? theme.colors.primary[700] : theme.colors.white};
            box-shadow: ${dark ? '0 2px 4px rgba(59, 130, 246, 0.2)' : '0 2px 4px rgba(255, 255, 255, 0.2)'};
            transform: translateY(-1px);
          }
        `;
      case 'success':
        return css`
          background-color: ${theme.colors.accent.success};
          color: ${theme.colors.white};
          border: 1px solid ${theme.colors.accent.success};
          box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);

          &:hover:not(:disabled) {
            background-color: #218838;
            border-color: #1e7e34;
            box-shadow: 0 4px 8px rgba(40, 167, 69, 0.4);
            transform: translateY(-1px);
          }
        `;
      case 'info':
        return css`
          background-color: ${theme.colors.accent.info};
          color: ${theme.colors.white};
          border: 1px solid ${theme.colors.accent.info};
          box-shadow: 0 2px 4px rgba(23, 162, 184, 0.3);

          &:hover:not(:disabled) {
            background-color: #138496;
            border-color: #117a8b;
            box-shadow: 0 4px 8px rgba(23, 162, 184, 0.4);
            transform: translateY(-1px);
          }
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          color: ${dark ? theme.colors.light.text.secondary : 'rgba(255, 255, 255, 0.7)'};
          border: 1px solid transparent;

          &:hover:not(:disabled) {
            background-color: ${dark ? theme.colors.light.background.secondary : 'rgba(255, 255, 255, 0.1)'};
            color: ${dark ? theme.colors.light.text.primary : theme.colors.white};
          }
        `;
      case 'danger':
        return css`
          background-color: ${theme.colors.accent.danger};
          color: ${theme.colors.white};
          border: 1px solid ${theme.colors.accent.danger};

          &:hover:not(:disabled) {
            background-color: #c82333;
            border-color: #bd2130;
          }
        `;
      default:
        return css`
          background-color: ${theme.colors.primary[500]};
          color: ${theme.colors.white};
          border: 1px solid ${theme.colors.primary[500]};
          box-shadow: ${theme.shadows.sm};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary[600]};
            border-color: ${theme.colors.primary[600]};
            box-shadow: ${theme.shadows.md};
            transform: translateY(-1px);
          }
        `;
    }
  }}

  &:focus {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  loading = false,
  disabled = false,
  dark = false,
  title,
  ...props
}) => {
  return (
    <StyledButton disabled={disabled || loading} dark={dark} title={title} {...props}>
      {loading && <LoadingSpinner />}
      {children}
    </StyledButton>
  );
};
